'use strict';

import * as Sentry from '@sentry/react';

/**
 * Fetch with bounded retries for transient failures.
 *
 * Retries when fetch rejects at the network level ("Failed to fetch" /
 * "Load failed") or the response status is 5xx. Anything below 500 is a
 * definitive server answer and returns immediately. AbortError is the
 * caller cancelling and is never retried.
 *
 * Only use this for idempotent requests: a network-level failure does not
 * prove the server never processed the request, so retrying a write can
 * duplicate it.
 *
 * @param {string} url
 * @param {Object} request - init object passed to fetch
 * @param {Object} options
 * @param {number[]} options.delays - ms to wait before each retry; length sets the retry count
 * @param {Function} options.shouldBail - checked after each delay; return true to stop retrying
 * @returns {Promise<Response>} the final response (may still be 5xx after exhausting retries)
 */
export function fetchWithRetry(url, request = {}, { delays = [500, 1500], shouldBail = () => false } = {}) {
    const attempts = delays.length + 1;

    const attemptFetch = (attempt) => {
        return fetch(url, request).then(
            (response) => {
                if (response.status < 500 || attempt === attempts) {
                    return response;
                }

                return retry(attempt, 'HTTP ' + response.status, { response });
            },
            (error) => {
                if (error.name === 'AbortError' || attempt === attempts) {
                    throw error;
                }

                return retry(attempt, error.message, { error });
            }
        );
    };

    const retry = (attempt, cause, { response = null, error = null }) => {
        Sentry.addBreadcrumb({
            category: 'fetch.retry',
            message: 'retrying failed fetch',
            level: 'warning',
            data: { url, attempt, cause },
        });

        return new Promise((resolve) => setTimeout(resolve, delays[attempt - 1])).then(() => {
            if (shouldBail()) {
                if (response) {
                    return response;
                }

                throw error;
            }

            return attemptFetch(attempt + 1);
        });
    };

    return attemptFetch(1);
}
